import type {
  DocumentStatus,
  FailedStep,
  FitStatus,
  ImageGateFlag,
  ImageReadStatus,
  ImageStatus,
  IssueKind,
  MarkKind,
  PrepStepKey,
  Provenance,
  Route,
  StatusGroup,
} from '@/lib/api/types'

export type Screen = 'preparing' | 'edit' | 'translating' | 'review'
export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'outline'

export interface StatusMeta {
  label: string
  tone: BadgeTone
}

/**
 * The single status -> screen mapping (03-frontend §4.2), the same as the server's `route`. The
 * client prefers `doc.route` and uses this only for optimistic transitions.
 */
export function canonicalScreen(d: { status: DocumentStatus; failure?: { step: FailedStep } | null }): Screen {
  switch (d.status) {
    case 'preparing':
      return 'preparing'
    case 'draft':
    case 'edited':
      return 'edit'
    case 'translating':
      return 'translating'
    case 'review':
    case 'done':
      return 'review'
    case 'failed':
      return d.failure?.step === 'preparation'
        ? 'preparing'
        : d.failure?.step === 'translation'
          ? 'translating'
          : 'review'
  }
}

const ROUTE_TO_SCREEN: Record<Route, Screen> = {
  preparation: 'preparing',
  editor: 'edit',
  translation: 'translating',
  review: 'review',
}

/** The screen for a document: the server's route when present, otherwise derived. */
export function screenFor(d: { status: DocumentStatus; route?: Route; failure?: { step: FailedStep } | null }): Screen {
  return (d.route && ROUTE_TO_SCREEN[d.route]) ?? canonicalScreen(d)
}

/** = API StatusGroup; the server computes it too. */
export const STATUS_GROUP = {
  in_progress: ['preparing', 'draft', 'edited', 'translating', 'failed'],
  in_review: ['review'],
  done: ['done'],
} as const satisfies Record<StatusGroup, readonly DocumentStatus[]>

/** Enums are open (x-extensible-enum): unknown values render with a neutral fallback. */
function lookup<K extends string>(map: Record<K, StatusMeta>, key: string, fallback?: string): StatusMeta {
  return (map as Record<string, StatusMeta>)[key] ?? { label: fallback ?? humanize(key), tone: 'neutral' }
}

function humanize(key: string): string {
  const s = key.replace(/[_.]+/g, ' ').trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const DOC_STATUS: Record<DocumentStatus, StatusMeta> = {
  preparing: { label: 'Preparing', tone: 'outline' },
  draft: { label: 'Draft', tone: 'neutral' },
  edited: { label: 'Edited', tone: 'neutral' },
  translating: { label: 'Translating', tone: 'accent' },
  review: { label: 'In review', tone: 'warning' },
  done: { label: 'Done', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
}
export const docStatusMeta = (s: string): StatusMeta => lookup(DOC_STATUS, s)

export const IMAGE_STATUS: Record<ImageStatus, StatusMeta> = {
  not_generated: { label: 'Not generated', tone: 'outline' },
  generating: { label: 'Generating', tone: 'accent' },
  ready: { label: 'Ready for review', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  keep: { label: 'Keep original', tone: 'neutral' },
}
export const imageStatusMeta = (s: string): StatusMeta => lookup(IMAGE_STATUS, s)

/** How a text item in a picture was read (image pipeline); `hint` is the tooltip sentence. */
export const READ_STATUS: Record<ImageReadStatus, StatusMeta & { hint: string; flagged: boolean }> = {
  verified: {
    label: 'Verified',
    tone: 'success',
    flagged: false,
    hint: 'Two readers agree on this text, with the same digits.',
  },
  second_read: {
    label: 'Read twice',
    tone: 'accent',
    flagged: false,
    hint: 'The AI read it twice, at different scales, with the same result.',
  },
  llm_read: {
    label: 'Read',
    tone: 'neutral',
    flagged: false,
    hint: 'Read by the AI reader.',
  },
  unverified: {
    label: 'Not verified',
    tone: 'warning',
    flagged: true,
    hint: 'Read on this computer only. It is not erased until you check it: correct it, or choose ✓ if it is right.',
  },
  // Older readings whose second read disagreed: used and typeset like any other.
  unreadable: {
    label: 'Read',
    tone: 'neutral',
    flagged: false,
    hint: 'Read by the AI reader.',
  },
}
export const readStatusMeta = (s: string) =>
  (READ_STATUS as Record<string, (typeof READ_STATUS)[ImageReadStatus] | undefined>)[s] ?? {
    label: s.replaceAll('_', ' '),
    tone: 'neutral' as const,
    flagged: false,
    hint: '',
  }

/** The automatic quality gate's per-unit flags on a generated version. */
export const GATE_FLAG: Record<ImageGateFlag, StatusMeta & { hint: string }> = {
  residue: { label: 'Text left behind', tone: 'danger', hint: 'Part of the original text is still visible.' },
  fill_color: { label: 'Background differs', tone: 'danger', hint: 'The erased area doesn’t match the cell colour.' },
  readback: { label: 'Reads back wrong', tone: 'warning', hint: 'The new text didn’t read back as intended.' },
  overflow: { label: 'Too long', tone: 'warning', hint: 'The translation needed more room than the box.' },
  keep_changed: {
    label: 'Kept area changed',
    tone: 'danger',
    hint: 'Pixels changed inside an area that should be kept.',
  },
}
export const gateFlagMeta = (s: string) =>
  (GATE_FLAG as Record<string, (typeof GATE_FLAG)[ImageGateFlag] | undefined>)[s] ?? {
    label: s.replaceAll('_', ' '),
    tone: 'warning' as const,
    hint: '',
  }

/** Translation provenance (HOW_LABEL, data.jsx:243, plus `memory`). */
export const PROVENANCE: Record<Provenance, StatusMeta> = {
  removed: { label: 'Removed', tone: 'neutral' },
  edited: { label: 'Edited', tone: 'accent' },
  auto: { label: 'Kept automatically', tone: 'neutral' },
  dnt: { label: 'Do not translate', tone: 'neutral' },
  glossary_dnt: { label: 'Do not translate · glossary', tone: 'neutral' },
  glossary: { label: 'Glossary', tone: 'success' },
  memory: { label: 'Machine translation · reused', tone: 'outline' },
  machine: { label: 'Machine translation', tone: 'outline' },
  untranslated: { label: 'Not translated', tone: 'warning' },
}
export const provenanceMeta = (s: string): StatusMeta => lookup(PROVENANCE, s)

export const MARK_KIND: Record<MarkKind | 'edited', StatusMeta> = {
  highlight: { label: 'Highlight', tone: 'warning' },
  comment: { label: 'Comment', tone: 'warning' },
  dnt: { label: 'Do not translate', tone: 'neutral' },
  remove: { label: 'Removed', tone: 'danger' },
  edited: { label: 'Edited', tone: 'accent' },
}
export const markKindMeta = (s: string): StatusMeta => lookup(MARK_KIND, s)

export const ISSUE_KIND: Record<IssueKind, StatusMeta> = {
  overflow: { label: 'Longer than its box', tone: 'danger' },
  untranslated: { label: 'Not translated', tone: 'warning' },
  glossary_missing: { label: 'Glossary term missing', tone: 'warning' },
  image_not_approved: { label: 'Image not approved', tone: 'warning' },
  image_outdated: { label: 'Image outdated', tone: 'warning' },
  comment: { label: 'Comment', tone: 'accent' },
  stale_edit: { label: 'Source changed', tone: 'warning' },
  font_fallback: { label: 'Font substituted', tone: 'neutral' },
  not_placed: { label: 'Not placed', tone: 'danger' },
  page_unreadable: { label: 'Page unreadable', tone: 'danger' },
  rebuild_failed: { label: "Page couldn't update", tone: 'danger' },
}
export const issueKindMeta = (s: string): StatusMeta => lookup(ISSUE_KIND, s)

export const FIT_STATUS: Record<FitStatus, StatusMeta> = {
  fits: { label: 'Fits', tone: 'success' },
  shrunk: { label: 'Shrunk to fit', tone: 'neutral' },
  overflow: { label: 'Longer than its box', tone: 'danger' },
  not_placed: { label: 'Not placed', tone: 'danger' },
  not_applicable: { label: 'Not applicable', tone: 'outline' },
}
export const fitStatusMeta = (s: string): StatusMeta => lookup(FIT_STATUS, s)

/** Preparation step labels (02-api §13.3 copy); the server sends `label` too and wins. */
export const PREP_STEP_LABEL: Record<PrepStepKey, string> = {
  upload: 'Uploading file',
  read_pages: 'Reading pages',
  extract_text: 'Extracting text from drawings',
  detect_structure: 'Detecting tables and title blocks',
  detect_images: 'Detecting images',
  find_repeated: 'Finding repeated text',
  render_pages: 'Rendering pages',
}
export const PREP_STEP_ORDER: readonly PrepStepKey[] = [
  'upload',
  'read_pages',
  'extract_text',
  'detect_structure',
  'detect_images',
  'find_repeated',
  'render_pages',
]

/** The PDF exists once the texts were translated. */
export function hasPdf(status: DocumentStatus): boolean {
  return status === 'review' || status === 'done'
}

/** Pictures and texts can still be changed: before translating and during review. */
export function editableStatus(status: DocumentStatus): boolean {
  return status === 'draft' || status === 'edited' || status === 'review'
}
